import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useDispatch } from 'store';
import { login } from 'store/usersReducer';
import YM from 'utils/metrics';
import { validateValue, validateForm } from 'utils/validation';
import Storage from 'utils/storage';
import Button from 'components/Button';
import Card from 'components/Card';
import Checkbox from 'components/Input/Checkbox';
import Input from 'components/Input';
import InputPassword from 'components/Input/InputPassword';
import ModalError from 'components/Modals/ModalError';
import classes from './login.module.scss';

const Login: React.FC = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const [loginData, setLoginData] = useState({
		email: '',
		password: '',
		rememberMe: Storage.getItem('rememberMe'),
	});
	const [errors, setErrors] = useState<{ [key: string]: string }>({
		email: '',
		password: '',
	});
	const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
	const [isLoading, setIsLoading] = useState(false);

	const validateFieldValue = (type: 'email' | 'password') => (): void => {
		const error = validateValue({
			type,
			value: loginData[type],
			isRequired: true,
		});
		setErrors({ ...errors, [type]: error });
	};

	const handleChange =
		(type: string) =>
		(value: string): void => {
			setLoginData({ ...loginData, [type]: value });
		};

	const handleChangeRememberMe = (value: boolean): void => {
		Storage.setItem('rememberMe', value);
		setLoginData({ ...loginData, rememberMe: value });
	};

	const handleSubmit = async (
		e: React.FormEvent<HTMLFormElement>
	): Promise<void> => {
		e.preventDefault();
		if (isLoading) return;

		const { isValid, formErrors } = validateForm({
			formData: { email: loginData.email, password: loginData.password },
			isRequired: true,
		});
		if (!isValid) {
			return setErrors(formErrors);
		}

		setIsLoading(true);
		try {
			await dispatch(login(loginData)).unwrap();
			YM.onFormSubmit();
			navigate('../');
		} catch (e) {
			setIsErrorModalOpen(true);
		}
		setIsLoading(false);
	};

	const handleToggleErrorModal = (): void => {
		setIsErrorModalOpen(!isErrorModalOpen);
	};

	const formInputs = (
		<div className={classes.formInputs}>
			<Input
				className={classes.field}
				value={loginData.email}
				onChange={handleChange('email')}
				type="text"
				placeholder="Электронная почта"
				error={errors.email}
				onBlur={validateFieldValue('email')}
			/>
			<InputPassword
				className={classes.field}
				value={loginData.password}
				onChange={handleChange('password')}
				placeholder="Пароль"
				error={errors.password}
				onBlur={validateFieldValue('password')}
			/>
			<Checkbox
				className={classes.rememberMe}
				value={loginData.rememberMe}
				onChange={handleChangeRememberMe}
				label="Запомнить меня"
			/>
		</div>
	);

	const formButtons = (
		<div className={classes.formButtons}>
			<Button
				className={classes.button}
				text="Войти"
				type="submit"
				isLoading={isLoading}
			/>
			<div className={classes.passwordRecoveryLink}>
				Забыли пароль?{' '}
				<Link className={classes.link} to="../password-recovery">
					Восстановить
				</Link>
			</div>
			<div className={classes.separator}>
				<div className={classes.separatorText}>Или</div>
			</div>
			<Button
				className={classes.buttonLink}
				text="Зарегистрироваться"
				href="../registration"
				styleType="secondary"
			/>
		</div>
	);

	const form = (
		<form className={classes.form} onSubmit={handleSubmit}>
			{formInputs}
			{formButtons}
		</form>
	);

	return (
		<Card
			className={classes.login}
			title="Авторизация"
			titleClassName={classes.title}
		>
			{form}
			<ModalError
				className={classes.modal}
				isOpen={isErrorModalOpen}
				onClose={handleToggleErrorModal}
				text="Неверная почта и/или пароль"
			/>
		</Card>
	);
};

export default Login;
